import React, { useState, useEffect } from 'react';
import { Keyboard, X } from 'lucide-react';

/**
 * KeyboardCheatsheet — small floating "?" button bottom-right of the app.
 * Click opens a popover with the keyboard shortcuts. Feb 16, 2026 UX #3.
 */
const SHORTCUTS = [
  { keys: ['Ctrl', 'G'], desc: 'Generate' },
  { keys: ['Ctrl', 'Shift', 'N'], desc: 'Next Patient' },
  { keys: ['Ctrl', 'S'], desc: 'Save Draft' },
  { keys: ['Ctrl', 'Shift', 'C'], desc: 'Copy All' },
  { keys: ['Esc'], desc: 'Clear Modal / Stop Dictation' },
  { keys: ['Ctrl', '/'], desc: 'Toggle this cheatsheet' },
  { keys: ['?'], desc: 'Open this cheatsheet' },
];

const KeyboardCheatsheet = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      // "?" key (Shift+/) opens the sheet — but skip when typing into inputs
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      if (e.key === '?' || (e.ctrlKey && e.key === '/')) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-10 h-10 rounded-full bg-card/90 backdrop-blur border border-border hover:border-primary/60 text-muted-foreground hover:text-primary shadow-lg flex items-center justify-center transition-colors"
        data-testid="keyboard-cheatsheet-trigger"
        title="Keyboard shortcuts (?)"
        aria-label="Open keyboard shortcuts"
      >
        <Keyboard className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9001]"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className="fixed bottom-16 right-4 z-[9002] w-[300px] max-w-[calc(100vw-2rem)] bg-card border border-border rounded-lg shadow-2xl p-3"
            role="dialog"
            aria-label="Keyboard shortcuts"
            data-testid="keyboard-cheatsheet-panel"
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-foreground flex items-center gap-1.5">
                <Keyboard className="w-3.5 h-3.5 text-primary" />
                Keyboard shortcuts
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground p-0.5"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="space-y-1.5">
              {SHORTCUTS.map((s, i) => (
                <li key={i} className="flex items-center justify-between text-xs gap-3">
                  <span className="text-muted-foreground flex-1 truncate">{s.desc}</span>
                  <span className="flex items-center gap-0.5">
                    {s.keys.map((k, ki) => (
                      <React.Fragment key={ki}>
                        {ki > 0 && <span className="text-muted-foreground/50 text-[10px]">+</span>}
                        <kbd className="px-1.5 py-0.5 rounded bg-secondary/50 border border-border text-foreground text-[10px] font-mono font-bold">
                          {k}
                        </kbd>
                      </React.Fragment>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-muted-foreground/70 italic mt-2 pt-2 border-t border-border">
              Tip: press <kbd className="px-1 py-px rounded bg-secondary/50 border border-border text-[9px]">?</kbd> anywhere to reopen
            </p>
          </div>
        </>
      )}
    </>
  );
};

export default KeyboardCheatsheet;
